/**
 * Two-state segmented toggle for draft visibility: Private (author only)
 * vs Shared (any editor can edit). Only meaningful while status='draft';
 * the editor hides it after publish.
 *
 * Styled to match the dark-fill preset chips elsewhere in the app
 * (#4c4c4c active, outlined inactive) per the global design tokens.
 */

import React from "react";
import { Box, Tooltip } from "@mui/material";

import { ResearchArticleVisibility } from "../../../../models/Research";

interface DraftVisibilityToggleProps {
  value: ResearchArticleVisibility;
  onChange: (next: ResearchArticleVisibility) => void;
  disabled?: boolean;
}

const OPTIONS: Array<{
  value: ResearchArticleVisibility;
  label: string;
  hint: string;
}> = [
  {
    value: "private",
    label: "Private",
    hint: "Only you can see and edit this draft",
  },
  {
    value: "shared",
    label: "Shared",
    hint: "Any research editor can see and edit",
  },
];

export default function DraftVisibilityToggle({
  value,
  onChange,
  disabled,
}: DraftVisibilityToggleProps) {
  return (
    <Box sx={{ display: "inline-flex", borderRadius: "3px", overflow: "hidden" }}>
      {OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <Tooltip key={opt.value} title={opt.hint} arrow>
            <Box
              onClick={() => !disabled && !active && onChange(opt.value)}
              sx={{
                px: 2,
                py: 0.5,
                fontSize: "0.8rem",
                fontWeight: active ? 700 : 500,
                cursor: disabled ? "not-allowed" : active ? "default" : "pointer",
                backgroundColor: active ? "#4c4c4c" : "transparent",
                color: active ? "#fff" : "text.primary",
                border: "1px solid #bdbdbd",
                opacity: disabled ? 0.5 : 1,
                "&:not(:first-of-type)": { borderLeft: "none" },
                "&:hover": {
                  backgroundColor: active ? "#4c4c4c" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              {opt.label}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
